import { getFormErrorMessage } from "@core/utility/get-error-message";
import { SplashScreenFormRule, SplashScreenFormSchema } from "@factory/form/form.rule";
import { useFormDefaultSplashScreen } from "@factory/form/form-default-splash-screen.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseSplashScreenModel } from "@factory/model/query-model-splash-screen";
import { useQyGetGoogleCloudAssetPreview } from "@factory/query/google-cloud-storage.query";
import { 
  useQyDeleteSplashScreen, 
  useQyGetSplashScreenById, 
  useQyUpdateSplashScreen} from "@factory/query/splash-screen.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditSplashScreen() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultSplashScreen();
  const navigate = useNavigate();
  const { mapUpdateSplashScreen, mapDeleteSplashScreen } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { splashScreenId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];
  const { mutate: viewAsset } = useQyGetGoogleCloudAssetPreview();

  // QUERY DECLARATION
  // API GETBYID SplashScreen
  const handleGetApiSuccess = (data: GetResponseSplashScreenModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    if (responseData?.image) {
      viewAsset(responseData?.image, {
        onSuccess(data) {
          const assetDisplay = URL.createObjectURL(data);
          setValue("file", assetDisplay);
        },
      });
    }

      setValue("name", responseData?.name || "");
      setValue("tenant", responseData?.tenant || "");
      setValue("image", responseData?.image || "");
      setValue("description", responseData?.description || "");
      setValue("link", responseData?.link || "");
  };
  const {
    data: splashScreenResponse,
    isLoading,
    isError: splashScreenError,
  } = useQyGetSplashScreenById(splashScreenId || "", handleGetApiSuccess);
  const splashScreenData = splashScreenResponse?.data?.[0];

  // API UPDATE SplashScreen
  const handleUpdateApiSuccess = () => {
    showSuccess("SplashScreen updated successfully");
  };
  const { mutate: updateSplashScreen, isLoading: isEditLoading } = useQyUpdateSplashScreen(
    handleUpdateApiSuccess
  );

  // API DELETE SplashScreen
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("SplashScreen deleted successfully");
  };
  const { mutate: deleteSplashScreen } = useQyDeleteSplashScreen(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<SplashScreenFormSchema>({
    defaultValues: getDefault(splashScreenData),
    resolver: zodResolver(SplashScreenFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: SplashScreenFormSchema) => {
    if (!splashScreenData || !splashScreenData.code) {
      throw new Error("No splashScreen data");
    }

    const formData = mapUpdateSplashScreen(form, splashScreenData.code);
    updateSplashScreen(formData);
  };
  const handleValidateError = (err: FieldErrors<SplashScreenFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!splashScreenData || !splashScreenData.code) {
      throw new Error("No splashScreen data found");
    }

    const form = mapDeleteSplashScreen(splashScreenData.code);
    deleteSplashScreen(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    splashScreenId,
    splashScreenData,
    isLoading,
    splashScreenError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
